import {
  Component,
  Host,
  ViewChild,
  ElementRef,
  Inject
} from '@angular/core';

import { NgClass, AsyncPipe } from '@angular/common';
import { PipesModule } from '../../../pipes/pipes.module';
import { IconComponent } from '../../icon/icon.component';
import { FocusOnDisabledDirective } from '../../focus/focus-on-disabled.directive';
import { DatePickerButtonComponent } from '../date-picker-button/date-picker-button.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';

import type { DateObject } from '../date-picker-inner/date-picker-inner.domain';
import type {
  OnInit } from '@angular/core';

@Component({
  selector: 'td-wb-year-picker',
  templateUrl: './year-picker.component.html',
  styleUrls: ['./year-picker.component.scss'],
  imports: [DatePickerButtonComponent, FocusOnDisabledDirective, IconComponent, NgClass, AsyncPipe, PipesModule]
})
export class YearPickerComponent implements OnInit {
  @ViewChild('yearButton',{ static:false, read: ElementRef }) yearButtonRef: ElementRef;

  /**
   * Date Picker Inner component
   */
  datePicker: DatePickerInnerComponent;

  /**
   * Title of year view
   */
  title: string;

  /**
   * Array of years to display
   */
  rows: Array<Array<DateObject>> = [];

  /**
   * Boolean for determining whether previous button should be disabled
   */
  navPrevDisabled: boolean;

  /**
   * Boolean for determining whether next button should be disabled
   */
  navNextDisabled: boolean;

  /**
   * The constructor of the component.
   *
   * @param datePicker Date picker inner component
   */
  constructor(@Inject(DatePickerInnerComponent) @Host() datePicker: DatePickerInnerComponent) {
    this.datePicker = datePicker;
  }

  /**
   * This function registers handlers and initializes step
   */
  ngOnInit(): void {
    this.datePicker.step.set('year', { years: this.datePicker.yearRange });
    this.datePicker.setRefreshViewHandler((): void => this.refreshView(), 'year');
    this.datePicker.setCompareHandler(
      (date1: Date, date2: Date): number => this.compareDates(date1, date2), 'year');
    this.datePicker.refreshView();
  }

  /**
   * This function is the refresh view handler for date picker inner component
   */
  refreshView(): void {
    const datePicker = this.datePicker;
    const years: Array<DateObject> = new Array(datePicker.yearRange);
    const start = this.getStartingYear(datePicker.activeDate.getFullYear());
    let date: Date;

    for (let i = 0; i < datePicker.yearRange; i++) {
      date = new Date(start + i, 0, 1);
      years[i] = datePicker.createDateObject(date, datePicker.formatYear);
      years[i].uid = datePicker.uniqueId + '-' + i;
    }

    // disable navigation according to the range
    // if the first day of the year range is not greater then minDate there is no point to navigate to a previous year range
    this.navPrevDisabled = (this.datePicker.minDate !== undefined) && (years[0].date <= this.datePicker.minDate);
    // if the first day of the next year range is greater then maxDate there is no point to navigate to the next year range
    this.navNextDisabled = (this.datePicker.maxDate !== undefined) &&
      (new Date(years[this.datePicker.yearRange - 1].date.getFullYear() + 1, 0, 1) > this.datePicker.maxDate);

    this.title = [years[0].label, years[datePicker.yearRange - 1].label].join(
      ' - '
    );
    this.rows = datePicker.split(years, 5) as Array<Array<DateObject>>;
  }

  /**
   * This function is the compare handler for date picker inner component
   *
   * @param date1 Date to compare
   * @param date2 Date to compare
   * @returns Returns comparison of two dates
   */
  compareDates(date1: Date, date2: Date): number {
    return date1.getFullYear() - date2.getFullYear();
  }

  /**
   * This function calculates the starting year based on year range
   *
   * @param year year number
   * @returns Returns starting year
   */
  protected getStartingYear(year: number): number {
    return (
      (year - 1) / this.datePicker.yearRange * this.datePicker.yearRange + 1
    );
  }
}
